/** Maintained collect-and-exit loop, extracted from the sidescroller starter.
 * Motion/collision remains createPlatformerMotionV2. Units: world units, seconds
 * for integration, integer ticks for all timers; player position is feet-centre.
 * No host, renderer, clock, RNG, provider or game-local callback dependency. */
import { createPlatformerMotionV2, type PlatformerBodyV1, type PlatformerMotionConfigV1, type PlatformerSurfaceV2 } from './motionBody2d.js';
import { createEventRuleDispatcherV1, SHORT_BOOST_EFFECT_PROFILE_V1, SHORT_BOOST_RULE_V1,
  type EventRuleStateV1, type EventRuleTransitionV1 } from './eventRules.js';

export type PlatformRunCommandV1 = { type: 'run'; dx: -1 | 0 | 1; jump: boolean };
export interface PlatformRunPointV1 { id: string; x: number; y: number }
export interface PlatformRunConfigV1 {
  profile: 'courier-short@1';
  physics: PlatformerMotionConfigV1;
  level: { height: number; pitY: number; solids: PlatformerSurfaceV2[]; parcels: PlatformRunPointV1[];
    spikes: PlatformRunPointV1[]; start: { x: number; y: number }; exit: { x: number; y: number } };
  rules: { lives: number; invulnerabilityTicks: number; hitFlashTicks: number; timeLimitTicks: number;
    parcelScore: number; exitScore: number };
}
export interface PlatformRunSnapshotV1 {
  tick: number;
  runner: PlatformerBodyV1 & { invulnerable: boolean; hitStunned: boolean };
  solids: PlatformerSurfaceV2[]; parcels: PlatformRunPointV1[]; spikes: PlatformRunPointV1[];
  exit: { x: number; y: number }; collectedIds: string[]; total: number;
  score: number; lives: number; hitsTaken: number; timeLeft: number;
  phase: 'active' | 'won' | 'lost'; lossCause: null | 'lives' | 'time';
}
export type PlatformRunEventV1 =
  | { kind: 'jump' | 'land'; tick: number; data: Record<string, never> }
  | { kind: 'parcel'; tick: number; data: { id: string; x: number; y: number; collected: number } }
  | { kind: 'hurt'; tick: number; data: { x: number; y: number; lives: number } }
  | { kind: 'won'; tick: number; data: { score: number } }
  | { kind: 'lost'; tick: number; data: { cause: 'lives' | 'time'; score: number } };

/** A fresh copy: consumers never share mutable configuration or match state. */
export function courierShortConfigV1(): PlatformRunConfigV1 {
  return {
    profile: 'courier-short@1',
    physics: { dt: 1 / 30, runSpeed: 7, gravity: 42, jumpSpeed: 15, maxFallSpeed: 24,
      jumpCut: 0.45, coyoteTicks: 4, width: 0.9, height: 1.4, worldWidth: 24 },
    level: { height: 9, pitY: 10,
      solids: [{ id: 'floor-a', mode: 'solid', x: 0, y: 7.5, w: 10, h: 1.5 },
        { id: 'floor-b', mode: 'solid', x: 13.2, y: 7.5, w: 10.8, h: 1.5 }],
      parcels: [{ id: 'parcel-1', x: 4, y: 6.9 }, { id: 'parcel-2', x: 16, y: 6.9 }, { id: 'parcel-3', x: 21, y: 6.9 }],
      spikes: [{ id: 'spike-1', x: 19, y: 7.5 }], start: { x: 1.5, y: 7.5 }, exit: { x: 23, y: 7.5 } },
    rules: { lives: 3, invulnerabilityTicks: 45, hitFlashTicks: 10, timeLimitTicks: 1200, parcelScore: 100, exitScore: 500 },
  };
}

function object(value: unknown, keys: readonly string[]): asserts value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || ![Object.prototype, null].includes(Object.getPrototypeOf(value))
    || Reflect.ownKeys(value).length !== keys.length
    || Object.keys(value).sort().join(',') !== [...keys].sort().join(',')
    || Object.values(Object.getOwnPropertyDescriptors(value)).some(d => !('value' in d))) {
    throw new RangeError('Invalid platform-run record');
  }
}
function finite(value: unknown, min: number, max: number): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) throw new RangeError('Invalid platform-run numeric bound');
}
/** Bounded maintained-config validator. Admission further restricts PR1 to the
 * exact courier config digest; these bounds also support isolated rule fixtures. */
export function validatePlatformRunConfigV1(input: unknown): PlatformRunConfigV1 {
  object(input, ['profile', 'physics', 'level', 'rules']);
  if (input.profile !== 'courier-short@1') throw new RangeError('Unsupported platform-run profile');
  object(input.physics, ['dt','runSpeed','gravity','jumpSpeed','maxFallSpeed','jumpCut','coyoteTicks','width','height','worldWidth']);
  const physics = input.physics;
  for (const key of Object.keys(physics)) finite(physics[key], 0, 120);
  if (physics.dt !== 1 / 30 || !Number.isSafeInteger(physics.coyoteTicks)) throw new RangeError('Platform-run requires fixed 30Hz');
  object(input.level, ['height','pitY','solids','parcels','spikes','start','exit']);
  const level = input.level;
  finite(level.height, 1, 32); finite(level.pitY, level.height, 64);
  for (const key of ['start','exit']) {
    object(level[key], ['x','y']); const point = level[key] as Record<string, unknown>;
    finite(point.x, Number(physics.width) / 2, Number(physics.worldWidth) - Number(physics.width) / 2);
    finite(point.y, Number(physics.height), level.height);
  }
  const ids = new Set<string>();
  for (const key of ['solids','parcels','spikes'] as const) {
    const entries = level[key];
    if (!Array.isArray(entries) || entries.length > (key === 'solids' ? 16 : 8) || (key !== 'spikes' && entries.length === 0)) throw new RangeError('Invalid platform-run entity count');
    for (const entry of entries) {
      object(entry, key === 'solids' ? ['id','mode','x','y','w','h'] : ['id','x','y']);
      if (typeof entry.id !== 'string' || !/^[a-z][a-z0-9-]{0,39}$/.test(entry.id) || ids.has(entry.id)) throw new RangeError('Invalid platform-run entity ID');
      ids.add(entry.id);
      finite(entry.x, 0, Number(physics.worldWidth)); finite(entry.y, 0, level.height);
      if (key === 'solids') {
        if (entry.mode !== 'solid') throw new RangeError('Platform-run V1 supports solid surfaces only');
        finite(entry.w, 0.01, Number(physics.worldWidth) - entry.x); finite(entry.h, 0.01, level.height - entry.y);
      }
    }
  }
  object(input.rules, ['lives','invulnerabilityTicks','hitFlashTicks','timeLimitTicks','parcelScore','exitScore']);
  for (const [key, value] of Object.entries(input.rules)) {
    finite(value, key === 'lives' || key === 'timeLimitTicks' ? 1 : 0, key === 'timeLimitTicks' ? 3600 : 1000);
    if (!Number.isSafeInteger(value)) throw new RangeError('Platform-run rules must be integer ticks/counts');
  }
  if (Number(input.rules.hitFlashTicks) > Number(input.rules.invulnerabilityTicks)) throw new RangeError('Hit flash exceeds grace');
  const config = structuredClone(input) as unknown as PlatformRunConfigV1;
  createPlatformerMotionV2(config.physics); // validate maintained motion bounds too
  for (const point of [config.level.start, config.level.exit]) {
    const half = config.physics.width / 2;
    if (!config.level.solids.some(s => point.x >= s.x && point.x <= s.x + s.w && Math.abs(point.y - s.y) < 1e-9)
      || config.level.solids.some(s => point.x + half > s.x && point.x - half < s.x + s.w && point.y > s.y && point.y - config.physics.height < s.y + s.h)) throw new RangeError('Spawn/exit requires unobstructed support');
  }
  for (const key of ['solids','parcels','spikes'] as const) config.level[key].sort((a,b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  return config;
}

function createPlatformRunControllerV1(input: PlatformRunConfigV1, effectProfile: null | 'short-boost@1') {
  const config = validatePlatformRunConfigV1(input);
  const { level, physics, rules } = config;
  const move = createPlatformerMotionV2(physics);
  const boostedMove = effectProfile === null ? null : createPlatformerMotionV2({ ...physics,
    runSpeed: physics.runSpeed * SHORT_BOOST_EFFECT_PROFILE_V1.multiplier });
  const effects = effectProfile === null ? null : createEventRuleDispatcherV1([SHORT_BOOST_RULE_V1]);
  const initialBody = (): PlatformerBodyV1 => ({ ...level.start, vx: 0, vy: 0, facing: 1, grounded: true, airborne: 0, jumpHeld: false });
  let body = initialBody();
  let tick = 0, score = 0, lives = rules.lives, grace = 0, hitsTaken = 0;
  let phase: PlatformRunSnapshotV1['phase'] = 'active';
  let lossCause: PlatformRunSnapshotV1['lossCause'] = null;
  let pending: PlatformRunCommandV1 | null = null;
  const collected = new Set<string>();
  let events: Array<PlatformRunEventV1 | EventRuleTransitionV1> = [];
  const nearY = (y: number) => y <= body.y + 0.4 && y >= body.y - physics.height - 0.4;
  const near = (p: { x: number; y: number }, reach = 0.4 + physics.width / 2) => Math.abs(p.x - body.x) <= reach && nearY(p.y);
  const lose = (cause: 'lives' | 'time') => { phase = 'lost'; lossCause = cause; events.push({ kind: 'lost', tick, data: { cause, score } }); };
  return {
    applyCommand(slot: number, command: unknown): boolean {
      if (slot !== 0 || phase !== 'active') return false;
      try { object(command, ['type','dx','jump']); } catch { return false; }
      if (command.type !== 'run' || ![-1,0,1].includes(command.dx as number) || typeof command.jump !== 'boolean') return false;
      pending = { type: 'run', dx: command.dx as -1|0|1, jump: command.jump }; return true;
    },
    step(): void {
      tick++;
      const command = pending ?? { type: 'run', dx: 0, jump: false };
      pending = null;
      if (phase !== 'active') return;
      if (effects) events.push(...effects.beginTick(tick));
      const effectState = effects?.snapshot(tick);
      const result = effectState?.active ? boostedMove!(body, command, level.solids) : move(body, command, level.solids); body = result.body;
      if (result.jumped) events.push({ kind: 'jump', tick, data: {} });
      if (result.landed) events.push({ kind: 'land', tick, data: {} });
      for (const parcel of level.parcels) if (!collected.has(parcel.id) && near(parcel)) {
        collected.add(parcel.id); score += rules.parcelScore;
        events.push({ kind: 'parcel', tick, data: { ...parcel, collected: collected.size } });
        if (effects) events.push(...effects.dispatch({ on: 'collected', target: 'player', tick }));
      }
      if (grace > 0) grace--;
      else if (body.y > level.pitY || level.spikes.some(p => near(p))) {
        lives--; hitsTaken++; grace = rules.invulnerabilityTicks;
        events.push({ kind: 'hurt', tick, data: { x: body.x, y: body.y, lives } });
        if (lives <= 0) lose('lives'); else body = initialBody();
      }
      if (phase !== 'active') return;
      if (collected.size === level.parcels.length && near(level.exit, 0.8)) {
        score += rules.exitScore; phase = 'won'; events.push({ kind: 'won', tick, data: { score } });
      } else if (tick >= rules.timeLimitTicks) lose('time');
    },
    snapshot(): PlatformRunSnapshotV1 {
      return structuredClone({ tick, runner: { ...body, invulnerable: grace > 0, hitStunned: grace > rules.invulnerabilityTicks - rules.hitFlashTicks },
        solids: level.solids, parcels: level.parcels.filter(p => !collected.has(p.id)), spikes: level.spikes, exit: level.exit,
        collectedIds: [...collected].sort(), total: level.parcels.length, score, lives, hitsTaken,
        timeLeft: Math.max(0, rules.timeLimitTicks - tick), phase, lossCause });
    },
    effectSnapshot(): EventRuleStateV1 | null { return effects?.snapshot(tick) ?? null; },
    drainEvents(): Array<PlatformRunEventV1 | EventRuleTransitionV1> { const out = events; events = []; return out; },
  };
}

/** Original courier surface. No config field can opt into maintained effects. */
export function createPlatformRunV1(input: PlatformRunConfigV1) {
  const loop = createPlatformRunControllerV1(input, null);
  return {
    applyCommand: loop.applyCommand,
    step: loop.step,
    snapshot: loop.snapshot,
    drainEvents(): PlatformRunEventV1[] { return loop.drainEvents() as PlatformRunEventV1[]; },
  };
}

/** Closed cross-module seam for the separately admitted maintained profile.
 * It accepts one compiled flavor ID, never callbacks, expressions or config. */
export function createMaintainedPlatformRunEffectV1(input: PlatformRunConfigV1, effectProfile: 'short-boost@1') {
  if (effectProfile !== 'short-boost@1') throw new RangeError('Unsupported maintained platform-run effect');
  return createPlatformRunControllerV1(input, effectProfile);
}
